import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { completeJob, getMyJobs, listQuotes, withdrawFromJob } from "../../lib/api/contractor";
import { startWorkOnJob } from "../jobs/startWork";
import {
  canStartWork,
  isAwaitingSignOff,
  parseJobBookingMode,
  parseJobStatus,
  parseQuoteStatus,
} from "../../lib/types/enums";
import type { Job, Quote } from "../../lib/types/models";
import { dateOnly, money, relative } from "../../lib/format";
import { JobCard } from "../../components/JobCard";
import {
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHead,
  StatusPill,
  SkeletonList,
  useToast,
} from "@ui/react";
import { ChatIcon, ChevronRightIcon, ToolsIcon } from "@ui/react/icons";
import { errorMessage } from "../../lib/errorMessage";
import { routerLink } from "../../lib/routerLink";

interface Loaded {
  jobs: Job[];
  quotes: Quote[];
}

interface Section {
  key: string;
  title: string;
  jobs: Job[];
}

/**
 * Everything on the contractor's plate, ordered by how badly it wants
 * attention: a held payout first, then work waiting on someone else, then work
 * waiting on them.
 *
 * A job lands in exactly one section — the first it qualifies for — so the
 * total in the page head counts jobs rather than memberships. The mobile app
 * lets a payment-issue job appear twice; here that would double-count and make
 * the header disagree with the sections beneath it.
 */
function group(jobs: Job[]): Section[] {
  const paymentIssue: Job[] = [];
  const awaitingSignOff: Job[] = [];
  const inProgress: Job[] = [];
  const confirming: Job[] = [];
  const readyToStart: Job[] = [];

  for (const job of jobs) {
    const status = parseJobStatus(job.status);
    if (job.paymentIssue) paymentIssue.push(job);
    else if (isAwaitingSignOff(status)) awaitingSignOff.push(job);
    else if (status === "inProgress") inProgress.push(job);
    // The homeowner has paid but the Paystack webhook hasn't landed yet —
    // genuinely nothing to do but wait, but the job must still land
    // SOMEWHERE or it (and the head count) silently disappears while a real
    // hold sits on the homeowner's card.
    else if (status === "paymentProviderPendingResult") confirming.push(job);
    // Funds-held (userPaid) sits here alongside accepted/scheduled-but-unpaid
    // jobs — canStartWork (userPaid only) decides which of them get the
    // button in footerFor, so an unpaid job still shows up instead of
    // vanishing, just without a way to start work with nothing held.
    else if (status === "userPaid" || status === "accepted" || status === "scheduled") {
      readyToStart.push(job);
    }
  }

  return [
    { key: "payment", title: "Payment issue", jobs: paymentIssue },
    { key: "signoff", title: "Awaiting sign-off", jobs: awaitingSignOff },
    { key: "progress", title: "In progress", jobs: inProgress },
    { key: "confirming", title: "Payment confirming", jobs: confirming },
    { key: "ready", title: "Ready to start", jobs: readyToStart },
  ];
}

export function ActiveJobsScreen() {
  const [state, setState] = useState<{ loading: boolean; error: unknown; data: Loaded | null }>({
    loading: true,
    error: null,
    data: null,
  });
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [busyJobId, setBusyJobId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [confirm, setConfirm] = useState<{ kind: "complete" | "withdraw"; job: Job } | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      // Quotes are supplementary: the mobile app swallows a /quotes failure and
      // still renders the jobs, and losing the whole screen because one
      // secondary call failed would be a worse trade here too.
      const [jobs, quotes] = await Promise.all([
        getMyJobs().then((r) => r.jobs ?? []),
        listQuotes().catch(() => [] as Quote[]),
      ]);
      setState({ loading: false, error: null, data: { jobs, quotes } });
    } catch (error) {
      setState({ loading: false, error, data: null });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const startWork = async (job: Job) => {
    setBusyJobId(job.jobId);
    setActionError(null);
    try {
      await startWorkOnJob(job);
      toast.show("Work started. The homeowner has been notified.");
      await load();
    } catch (error) {
      setActionError(error);
    } finally {
      setBusyJobId(null);
      setConfirm(null);
    }
  };

  const markComplete = async (job: Job) => {
    setBusyJobId(job.jobId);
    setActionError(null);
    try {
      await completeJob(job.jobId);
      toast.show("Job marked as complete. Awaiting homeowner sign-off.");
      await load();
    } catch (error) {
      setActionError(error);
    } finally {
      setBusyJobId(null);
      setConfirm(null);
    }
  };

  /** Hands the job back to the marketplace — see `withdrawFromJob`. A re-read
   *  rather than a local edit: the job leaves this screen entirely, and so does
   *  the quote, which the server has just rejected. */
  const withdraw = async (job: Job) => {
    setBusyJobId(job.jobId);
    setActionError(null);
    try {
      await withdrawFromJob(job.jobId);
      toast.show("You've withdrawn from this job. The homeowner has been told.");
      await load();
    } catch (error) {
      setActionError(error);
    } finally {
      setBusyJobId(null);
      setConfirm(null);
    }
  };

  const jobs = state.data?.jobs ?? [];
  const sections = group(jobs).filter((section) => section.jobs.length > 0);
  const placed = new Set(sections.flatMap((section) => section.jobs.map((job) => job.jobId)));

  // A pending quote whose job already has a section is not "awaiting a
  // response" — the homeowner has answered by accepting it.
  const awaitingResponse = (state.data?.quotes ?? []).filter(
    (quote) => parseQuoteStatus(quote.status) === "pending" && !placed.has(quote.jobId),
  );

  const total = placed.size + awaitingResponse.length;

  const messageLink = (job: Job) =>
    job.conversationId ? (
      <Link className="btn btn-secondary" to={`/messages/${job.conversationId}`}>
        <ChatIcon />
        Message
      </Link>
    ) : null;

  /* Per-ROW actions are secondary, never the gradient. One card's "Mark
     complete" was a primary, so a screen with four jobs in progress showed four
     orange gradients competing with each other and with the status pills —
     R4's "one primary per screen state" broken by repetition rather than by a
     second button. The real primary is the ConfirmDialog's confirm, which is
     where the decision is actually taken. */
  const footerFor = (section: Section, job: Job) => {
    const busy = busyJobId === job.jobId;
    if (section.key === "payment") {
      return (
        <>
          <StatusPill tone="danger">Payout on hold</StatusPill>
          {messageLink(job)}
        </>
      );
    }
    if (section.key === "progress") {
      return (
        <>
          {messageLink(job)}
          <Button
            variant="secondary"
            onClick={() => setConfirm({ kind: "complete", job })}
            disabled={busy}
          >
            Mark complete
          </Button>
        </>
      );
    }
    if (section.key === "ready") {
      const status = parseJobStatus(job.status);
      const bookingMode = parseJobBookingMode(job.bookingMode);
      // This section also holds accepted/scheduled jobs with nothing held yet,
      // so not every card here can start. A paid direct booking ALSO reads
      // "scheduled" — bookingMode is what tells the two apart, so the note is
      // now shown only where it is actually true, and the button appears for a
      // paid booking instead of the screen going silent on both.
      if (!canStartWork(status, bookingMode)) {
        // This branch IS the withdrawable set, and the server's guard says so
        // in the same words: accepted or scheduled with nothing held. (userPaid
        // always starts, and a paid direct booking reads as funds-held too, so
        // neither reaches here.) Without it a contractor's only exit from a job
        // he cannot start and cannot finish is talking the homeowner into
        // cancelling it, in chat.
        return (
          <>
            {messageLink(job)}
            <span className="label-sm muted">Waiting for the homeowner's payment</span>
            <Button
              variant="secondary"
              onClick={() => setConfirm({ kind: "withdraw", job })}
              disabled={busy}
            >
              Withdraw
            </Button>
          </>
        );
      }
      return (
        <>
          {messageLink(job)}
          <Button variant="secondary" onClick={() => void startWork(job)} loading={busy}>
            Start work
          </Button>
        </>
      );
    }
    return messageLink(job);
  };

  /* The head is rendered BEFORE the states rather than after them. Returning
     the skeleton or the error early left those states with no <h1> at all — a
     document whose only heading was "We couldn't load this", and a screen
     reader landing on a page that would not say which one it was. The states
     are what changes here; the name of the screen is not. */
  return (
    <div className="stack stack--loose">
      <PageHead
        title="Active work"
        lead={total > 0 ? `${total} item${total === 1 ? "" : "s"} on your plate` : undefined}
      />

      {actionError ? (
        <Banner tone="danger" title="That didn't work">
          {errorMessage(actionError)}
        </Banner>
      ) : null}

      {state.loading ? (
        <div className="stack" role="status">
          <span className="sr-only">Loading your active work</span>
          <SkeletonList rows={4} height="8rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={() => void load()} />
      ) : total === 0 ? (
        <EmptyState
          icon={<ToolsIcon />}
          title="No active work"
          action={{ label: "Find jobs", href: "/jobs" }}
          renderLink={routerLink}
        >
          Jobs you're working on will appear here. Browse available work to get started.
        </EmptyState>
      ) : (
        <div className="stack">
          {sections.map((section) => {
            const open = !collapsed[section.key];
            return (
              <div key={section.key}>
                <h2 className="headline-sm">
                  <button
                    type="button"
                    className="collapsible__trigger"
                    aria-expanded={open}
                    aria-controls={`section-${section.key}`}
                    onClick={() =>
                      setCollapsed((current) => ({ ...current, [section.key]: open }))
                    }
                  >
                    <ChevronRightIcon />
                    <span>{section.title}</span>
                    <span className="collapsible__count">{section.jobs.length}</span>
                  </button>
                </h2>
                {/* `hidden` alone would lose to `.stack { display: flex }` — an
                    author rule beats the UA sheet whatever the specificity — so the
                    display is forced inline as well as declared semantically. */}
                <div
                  className="stack"
                  id={`section-${section.key}`}
                  hidden={!open}
                  style={open ? undefined : { display: "none" }}
                >
                  {section.jobs.map((job) => (
                    <JobCard
                      key={job.jobId}
                      job={job}
                      href={`/jobs/${job.jobId}`}
                      footer={footerFor(section, job)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {awaitingResponse.length > 0 ? (
            <div>
              <h2 className="headline-sm">
                <button
                  type="button"
                  className="collapsible__trigger"
                  aria-expanded={!collapsed["quotes"]}
                  aria-controls="section-quotes"
                  onClick={() =>
                    setCollapsed((current) => ({ ...current, quotes: !current["quotes"] }))
                  }
                >
                  <ChevronRightIcon />
                  <span>Awaiting response</span>
                  <span className="collapsible__count">{awaitingResponse.length}</span>
                </button>
              </h2>
              <div
                className="stack"
                id="section-quotes"
                hidden={Boolean(collapsed["quotes"])}
                style={collapsed["quotes"] ? { display: "none" } : undefined}
              >
                {awaitingResponse.map((quote) => (
                  <div key={quote.quoteId} className="row">
                    <div className="stack stack--tight">
                      <div className="row__top">
                        <StatusPill tone="pending">Awaiting response</StatusPill>
                        <span className="amount">{money(quote.amount, quote.currency)}</span>
                      </div>
                      {quote.description ? (
                        <p className="muted clamp-2">{quote.description}</p>
                      ) : null}
                      <div className="cluster label-sm">
                        {quote.estimatedDays > 0 ? (
                          <span>
                            {quote.estimatedDays} day{quote.estimatedDays === 1 ? "" : "s"}{" "}
                            estimated
                          </span>
                        ) : null}
                        {quote.validUntil ? (
                          <span>Valid until {dateOnly(quote.validUntil)}</span>
                        ) : null}
                        <span>Sent {relative(quote.createdAt)}</span>
                      </div>
                      <div className="cluster">
                        <Link className="btn btn-secondary" to={`/jobs/${quote.jobId}`}>
                          View job
                        </Link>
                        {quote.conversationId ? (
                          <Link
                            className="btn btn-tertiary"
                            to={`/messages/${quote.conversationId}`}
                          >
                            <ChatIcon />
                            Open conversation
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {confirm?.kind === "complete" ? (
        <ConfirmDialog
          title="Mark this job complete?"
          body="The homeowner will be asked to review the work and sign off before your payment is released."
          confirmLabel="Mark complete"
          confirming={busyJobId === confirm.job.jobId}
          onConfirm={() => void markComplete(confirm.job)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {confirm?.kind === "withdraw" ? (
        <ConfirmDialog
          title="Withdraw from this job?"
          confirmLabel="Withdraw"
          cancelLabel="Keep the job"
          destructive
          confirming={busyJobId === confirm.job.jobId}
          onConfirm={() => void withdraw(confirm.job)}
          onCancel={() => setConfirm(null)}
          body={
            <>
              <p>
                The job goes back on the marketplace for other contractors to quote on, and
                the homeowner is told in your conversation.
              </p>
              <p>Your quote is withdrawn with it. You can quote again if you change your mind.</p>
            </>
          }
        />
      ) : null}
    </div>
  );
}
